import { useAssignInfo } from "@saleor/accountSettings/misc";
import { isSuperUser } from "@saleor/auth/misc";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState,
  PaginationState
} from "@saleor/hooks/usePaginator";
import useUser from "@saleor/hooks/useUser";
import { endDateTime, findInEnum, maybe, startDateTime } from "@saleor/misc";
import RemittanceRunnerDownloadDialog from "@saleor/remittanceRunner/components/RemittanceRunnerDownloadDialog";
import {
  getFilterOptsForDispatcher,
  getFilterOptsForNonDispatcher
} from "@saleor/remittanceRunner/filters/filterOpts";
import { getFilterQueryParam } from "@saleor/remittanceRunner/filters/filterQueryParams";
import {
  useAccountAssignedMall,
  useRemittanceRunnerMallFilterDataQuery,
  useRemittanceRunnerRemittances
} from "@saleor/remittanceRunner/queries";
import {
  remittanceRunnerListUrl,
  RemittanceRunnerListUrlDialog,
  RemittanceRunnerListUrlQueryParams
} from "@saleor/remittanceRunner/url";
import {
  convertStringToBoolean,
  getMomentDate,
  isInvalidThreeMonth,
  isRunnerRider,
  REMITTANCE_DEFAULT_DELIVERY_TYPES
} from "@saleor/remittanceRunner/utils/handlers";
import usePaginatorRemittance from "@saleor/remittanceRunner/utils/paginators";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import {
  CourierTypeEnum,
  OrderDirection,
  RemittanceSortField,
  RemittanceStatusEnum,
  SmopOrderStatusEnum,
  SmopStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import * as React from "react";
import { useIntl } from "react-intl";

import RemittanceRunnerListPage from "../../components/RemittanceRunnerListPage";
import { useRemittanceRunnerOrders } from "../../queries";

interface RemittanceRunnerListProps {
  params: RemittanceRunnerListUrlQueryParams;
}

const RemittanceRunnerList: React.FC<RemittanceRunnerListProps> = ({
  params
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const navigate = useNavigator();
  const paginate = usePaginator();
  const paginateRemittance = usePaginatorRemittance();
  const user = useUser();

  const userType = user?.user?.userType?.code;
  const isDispatcher = userType && isRunnerRider(userType);
  const isOmiAdmin = userType === SmopUserTypeCode.OMI_ADMIN;
  const userInfo = useAssignInfo(user?.user);
  const today = getMomentDate("YYYY-MM-DD");
  const ninetyDaysAgoDate = getMomentDate("YYYY-MM-DD", -90);

  const currencySymbol = "PHP";
  const { updateListSettings, settings } = useListSettings(
    ListViews.REMITTANCE_LIST
  );

  const sortBy = {
    sortBy: {
      direction: OrderDirection.DESC,
      field: RemittanceSortField.CREATION_DATE
    }
  };

  const getStatusNoFromParams = () => {
    let statusNo = 0;
    if (params.activeTab === undefined && params?.status !== undefined) {
      if (params?.status === "TO_REMIT") {
        statusNo = 1;
      } else if (params?.status === "FOR_BANK_CONFIRMATION") {
        statusNo = 2;
      } else if (params?.status === "REMITTED") {
        statusNo = 3;
      }
    } else if (params.activeTab !== undefined) {
      statusNo = parseInt(params.activeTab, 0);
    }
    return statusNo;
  };

  const getStatusValueFromNo = tabNo => {
    let remittanceStatusArray = [];
    if (tabNo === 1) {
      remittanceStatusArray = [RemittanceStatusEnum.TO_REMIT];
    } else if (tabNo === 2) {
      remittanceStatusArray = [RemittanceStatusEnum.FOR_BANK_CONFIRMATION];
    } else if (tabNo === 3) {
      remittanceStatusArray = [RemittanceStatusEnum.REMITTED];
    }
    return remittanceStatusArray;
  };

  const getHasRemittanceIdFromParams = () => {
    let paramValue = null;
    if (params?.status === "NO_REMITTANCE_ID") {
      paramValue = false;
    } else if (params?.status !== undefined) {
      paramValue = true;
    }
    return paramValue;
  };

  const getHasReceiptFromParams = () => {
    let paramValue = null;
    if (params?.hasReceipt === "true") {
      paramValue = true;
    } else if (params?.hasReceipt !== undefined) {
      paramValue = false;
    }
    return paramValue;
  };

  const [remittanceList, setRemittanceList] = React.useState(null);
  const [remittanceOrderList, setRemittanceOrderList] = React.useState(null);
  const [currentTab, setCurrentTab] = React.useState(getStatusNoFromParams());
  const [tabStatus, setTabStatus] = React.useState<RemittanceStatusEnum[]>(
    getStatusValueFromNo(currentTab)
  );
  const [searchQuery, setSearchQuery] = React.useState("");
  const [queryVariables, setQueryVariables] = React.useState<PaginationState>({
    first: settings.rowNumber
  });
  const [
    queryVariablesRemittance,
    setQueryVariablesRemittance
  ] = React.useState<PaginationState>({ first: settings.rowNumber });

  const [motherOrder, setMotherOrder] = React.useState(params?.motherOrder);

  const [mall, setMall] = React.useState(params?.mall);

  const [remittanceDateFrom, setRemittanceDateFrom] = React.useState(
    params?.remittanceDateFrom
  );
  const [remittanceDateTo, setRemittanceDateTo] = React.useState(
    params?.remittanceDateTo
  );
  const [remitterFirstName, setRemitterFirstName] = React.useState(
    params?.remitterFirstName
  );
  const [remitterLastName, setRemitterLastName] = React.useState(
    params?.remitterLastName
  );
  const [remittanceId, setRemittanceId] = React.useState(params?.remittanceId);
  const [dispatcherFirstName, setDispatcherFirstName] = React.useState(
    params?.dispatcherFirstName
  );
  const [dispatcherLastName, setDispatcherLastName] = React.useState(
    params?.dispatcherLastName
  );
  const [hasRemittanceId, setHasRemittanceId] = React.useState(
    getHasRemittanceIdFromParams()
  );
  const [isPaymentCollected, setIsPaymentCollected] = React.useState(
    convertStringToBoolean(params?.isPaymentCollected)
  );
  const [createdDateFrom, setCreatedDateFrom] = React.useState(
    params?.createdDateFrom ? params?.createdDateFrom : ninetyDaysAgoDate
  );
  const [createdDateTo, setCreatedDateTo] = React.useState(
    params?.createdDateTo ? params?.createdDateTo : today
  );
  const [hasReceipt, setHasReceipt] = React.useState(getHasReceiptFromParams());

  const [companyName, setCompanyName] = React.useState(params?.companyName);

  const [openDownloadDialog, closeDownloadDialog] = createDialogActionHandlers<
    RemittanceRunnerListUrlDialog,
    RemittanceRunnerListUrlQueryParams
  >(navigate, remittanceRunnerListUrl, params);

  React.useEffect(() => {
    setMotherOrder(params?.motherOrder);
    setRemittanceDateFrom(params?.remittanceDateFrom);
    setRemittanceDateTo(params?.remittanceDateTo);
    setRemitterFirstName(params?.remitterFirstName);
    setRemitterLastName(params?.remitterLastName);
    setRemittanceId(params?.remittanceId);
    setDispatcherFirstName(params?.dispatcherFirstName);
    setDispatcherLastName(params?.dispatcherLastName);
    setMall(params?.mall);
    setIsPaymentCollected(convertStringToBoolean(params?.isPaymentCollected));
    setCreatedDateFrom(
      params?.createdDateFrom ? params?.createdDateFrom : ninetyDaysAgoDate
    );
    setCreatedDateTo(params?.createdDateTo ? params?.createdDateTo : today);
    setHasReceipt(getHasReceiptFromParams());
    setCompanyName(params?.companyName);

    if (params?.status === "NO_REMITTANCE_ID") {
      setHasRemittanceId(false);
      setCurrentTab(0);
      setTabStatus(getStatusValueFromNo(0));
    } else if (params.status !== undefined) {
      let paramStatus = 0;
      if (params?.status === "TO_REMIT") {
        paramStatus = 1;
      } else if (params?.status === "FOR_BANK_CONFIRMATION") {
        paramStatus = 2;
      } else if (params?.status === "REMITTED") {
        paramStatus = 3;
      }
      setHasRemittanceId(null);
      setCurrentTab(paramStatus);
      setTabStatus(getStatusValueFromNo(paramStatus));
    } else {
      setHasRemittanceId(null);
    }
  }, [params]);

  // get the user assign mall
  const {
    data: userAssignedMall,
    refetch: refetchUserMall
  } = useAccountAssignedMall({
    displayLoader: true,
    skip: !userInfo?.mall,
    variables: {
      id: userInfo?.mall
    }
  });
  const isEnableMall = userAssignedMall?.mall?.remittanceConfig?.isEnable;
  const isEmptyAssignMall = userAssignedMall?.mall === null;

  // NON DISPATCHER
  const paginationState =
    createPaginationState(settings.rowNumber, params)?.first !== null
      ? createPaginationState(settings.rowNumber, params)
      : { first: settings.rowNumber };

  const nonDispatcherFilters = {
    companyName: maybe(
      () => companyName.map(name => findInEnum(name, CourierTypeEnum)),
      []
    ),
    createdAt: {
      gte: startDateTime(ninetyDaysAgoDate),
      lte: endDateTime(today)
    },
    deliveryTypes: REMITTANCE_DEFAULT_DELIVERY_TYPES,
    hasReceipt,
    hasRemittanceId,
    isPaymentCollected,
    malls: mall,
    orderCode: motherOrder,
    paymentType: "cash",
    remittanceId,
    remittedAt: {
      gte: remittanceDateFrom ? startDateTime(remittanceDateFrom) : null,
      lte: remittanceDateTo ? endDateTime(remittanceDateTo) : null
    },
    remitter: {
      firstName: remitterFirstName,
      lastName: remitterLastName
    },
    search: searchQuery,
    smopStatus: [SmopOrderStatusEnum.COMPLETED, SmopOrderStatusEnum.SHIPPED],
    status: tabStatus
  };

  const {
    data: remittanceOrders,
    refetch: refetchRemittanceOrders,
    loading: remittanceOrdersLoading
  } = useRemittanceRunnerOrders({
    displayLoader: true,
    skip: isDispatcher,
    variables: {
      ...queryVariables,
      ...nonDispatcherFilters
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    remittanceOrders?.remittanceOrders?.pageInfo,
    paginationState,
    params
  );

  const onSearchChange = searchText => {
    setSearchQuery(searchText);
    refetchRemittanceOrders();
  };

  const onSearchChangeRemittance = searchText => {
    setSearchQuery(searchText);
    refetchRemittances();
  };

  React.useEffect(() => {
    setRemittanceOrderList(remittanceOrders?.remittanceOrders);
  }, [remittanceOrders]);

  const onRefetchOrders = () => {
    refetchRemittanceOrders();
  };

  // DISPATCHER
  const paginationStateRemittance =
    createPaginationState(settings.rowNumber, params)?.first !== null
      ? createPaginationState(settings.rowNumber, params)
      : { first: settings.rowNumber };

  const {
    data: remittances,
    refetch: refetchRemittances,
    loading: remittanceIsLoading
  } = useRemittanceRunnerRemittances({
    displayLoader: true,
    skip: !isDispatcher,
    variables: {
      ...queryVariablesRemittance,
      ...sortBy,
      createdAt: {
        gte: createdDateFrom ? startDateTime(createdDateFrom) : null,
        lte: createdDateTo ? endDateTime(createdDateTo) : null
      },
      hasReceipt,
      remittanceId,
      remittedAt: {
        gte: remittanceDateFrom ? startDateTime(remittanceDateFrom) : null,
        lte: remittanceDateTo ? endDateTime(remittanceDateTo) : null
      },
      remitter: {
        firstName: dispatcherFirstName,
        lastName: dispatcherLastName
      },
      search: searchQuery,
      status: tabStatus
    }
  });

  const {
    loadNextPageRemittance,
    loadPreviousPageRemittance,
    pageInfoRemittance
  } = paginateRemittance(
    remittances?.remittances?.pageInfo,
    paginationStateRemittance,
    params
  );

  React.useEffect(() => {
    setRemittanceList(remittances?.remittances);
  }, [remittances]);

  const onRefetch = () => {
    refetchRemittances();
  };

  // SHARED STATE CHANGES EVENTS
  React.useEffect(() => {
    navigate(
      remittanceRunnerListUrl({
        activeTab: currentTab,
        ...params,
        ...DEFAULT_INITIAL_PAGINATION_DATA
      }),
      true
    );
  }, [settings.rowNumber]);

  React.useMemo(() => {
    if (!isDispatcher) {
      setQueryVariables(paginationState);
    } else {
      setQueryVariablesRemittance(paginationStateRemittance);
    }
  }, [params]);

  const tabChanged = (tabIndex, tabValue) => {
    refetchUserMall();
    setHasRemittanceId(null);
    setCurrentTab(tabIndex);
    setTabStatus(tabValue);
    resetFilters();

    navigate(
      remittanceRunnerListUrl({
        activeTab: tabIndex.toString(),
        ...{},
        ...DEFAULT_INITIAL_PAGINATION_DATA
      }),
      true
    );
  };

  const { data: initialFilterData } = useRemittanceRunnerMallFilterDataQuery({
    variables: {
      first: params?.mall && params.mall.length > 0 ? params.mall.length : 5,
      mall: params.mall
    }
  });

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: SmopStatusEnum.ACT,
      first: 5
    }
  });

  const filterOpts = !isDispatcher
    ? getFilterOptsForNonDispatcher(params, {
        initial: initialFilterData?.malls?.edges?.map(edge => edge.node) || [],
        search: searchMall
      })
    : getFilterOptsForDispatcher(params);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: remittanceRunnerListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const dateFilterExceeds90Days = (dateFrom, dateTo) => {
    if (isInvalidThreeMonth(dateFrom, dateTo)) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    if (
      dateFilterExceeds90Days(
        params.remittanceDateFrom,
        params.remittanceDateTo
      )
    ) {
      navigate(remittanceRunnerListUrl());
    }
  }, [params.remittanceDateFrom, params.remittanceDateTo]);

  React.useEffect(() => {
    if (dateFilterExceeds90Days(params.createdDateFrom, params.createdDateTo)) {
      navigate(remittanceRunnerListUrl());
    }
  }, [params.createdDateFrom, params.createdDateTo]);

  return (
    <>
      <RemittanceRunnerListPage
        onDownloadClick={() => openDownloadDialog("download")}
        remittances={remittanceList}
        remittanceOrders={remittanceOrderList}
        currencySymbol={currencySymbol}
        filterOpts={filterOpts}
        onFilterChange={e => {
          if (params?.before || params?.after) {
            delete params.before;
            delete params.after;
            navigate(
              remittanceRunnerListUrl({
                ...params,
                ...DEFAULT_INITIAL_PAGINATION_DATA
              }),
              true
            );
          }

          e.forEach(item => {
            if (item.name === "companyName" && Array.isArray(item.value)) {
              item.value = item.value.filter(val => val != null);
            }
          });
          changeFilters(e);
        }}
        onRefetchOrders={!isDispatcher ? onRefetchOrders : onRefetch}
        search={searchQuery}
        searchPlaceholder={"Search"}
        onSearchChange={val =>
          !isDispatcher ? onSearchChange(val) : onSearchChangeRemittance(val)
        }
        onUpdateListSettings={updateListSettings}
        settings={settings}
        pageInfo={!isDispatcher ? pageInfo : pageInfoRemittance}
        onPreviousPage={
          !isDispatcher ? loadPreviousPage : loadPreviousPageRemittance
        }
        brands={undefined}
        onAdd={null}
        disabled={false}
        onRowClick={null}
        onNextPage={!isDispatcher ? loadNextPage : loadNextPageRemittance}
        isDispatcher={isDispatcher}
        isOmiAdmin={isOmiAdmin}
        isSuperUser={isSuperUser(user?.user)}
        currentTab={currentTab}
        tabChanged={tabChanged}
        isLoadingRemittanceOrders={remittanceIsLoading}
        isLoadingRemittance={remittanceOrdersLoading}
        isEmptyMall={isEmptyAssignMall}
        isEnableMall={isEnableMall}
      />

      <RemittanceRunnerDownloadDialog
        params={params}
        totalRows={remittanceOrders?.remittanceOrders?.totalCount}
        closeModal={closeDownloadDialog}
        filters={nonDispatcherFilters}
      />
    </>
  );
};

export default RemittanceRunnerList;
